import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { DataRoomTeaser } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface RestoreTeaserDialogProps {
	dataRoomGuid: string | undefined;
	teaser: DataRoomTeaser | null;
	close: () => void;
}

export const RestoreTeaserDialog: React.FC<RestoreTeaserDialogProps> = ({ dataRoomGuid, teaser, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onRemoveConfirmation = () => {
		if (teaser !== null) removeTeaser(teaser);
	};

	const { mutate: removeTeaser } = useMutation<DataRoomTeaser, unknown, DataRoomTeaser>(
		teaserToDelete =>
			getResponseData(
				api.delete<DataRoomTeaser>(CerebroApiEndpoints.Teasers.Specific(dataRoomGuid, teaserToDelete.guid))
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: (_data, deletedTeaser) => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar('Teaser Restored.', { variant: 'success' });

				close();
			},
			onError: () => {
				client.invalidateQueries(QueryKeys.Teasers.Root(dataRoomGuid));
				client.invalidateQueries(QueryKeys.DataRoom.Confirmation(dataRoomGuid));
				snackbar.enqueueSnackbar('There was a problem deleting the teaser.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={teaser !== null} onClose={close}>
			<DialogTitle>Confirm Restore</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to restore this deleted teaser for {teaser?.firstName} {teaser?.lastName} at{' '}
					{teaser?.bankName}?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onRemoveConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
